"use client";

import { useState, useEffect, useRef, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import * as z from "zod/mini";
import { authClient } from "@/lib/auth-client";
import { getPasswordStrength, STRENGTH_COLORS } from "@/lib/password-strength";
import { signUpSchema } from "@/lib/schemas/auth-schemas";
import TurnstileWidget, { type TurnstileWidgetHandle } from "@/components/TurnstileWidget";
import { VERIFY_CALLBACK_URL } from "@/lib/constants";
import { SIGNUP_SOURCE_COOKIE, isValidSignupSource } from "@/lib/signup-source";
import { track } from "@/lib/analytics";

function SourceCapture() {
  const searchParams = useSearchParams();
  useEffect(() => {
    const src = searchParams.get("src");
    if (isValidSignupSource(src)) {
      document.cookie = `${SIGNUP_SOURCE_COOKIE}=${encodeURIComponent(src)}; path=/; max-age=${30 * 24 * 3600}; SameSite=Lax`;
    }
    track("signup_started", isValidSignupSource(src) ? { src } : {});
  }, [searchParams]);
  return null;
}

export default function SignupPage() {
  const router = useRouter();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const strength = getPasswordStrength(form.password);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  }

  function validateField(name: string) {
    const result = signUpSchema.safeParse(form);
    if (!result.success) {
      const fieldError = z.flattenError(result.error).fieldErrors[name as keyof typeof form]?.[0];
      if (fieldError) setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const result = signUpSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors = z.flattenError(result.error).fieldErrors;
      setErrors({
        name: fieldErrors.name?.[0] ?? "",
        email: fieldErrors.email?.[0] ?? "",
        password: fieldErrors.password?.[0] ?? "",
      });
      return;
    }

    setLoading(true);
    setServerError("");

    const { error } = await authClient.signUp.email({
      name: result.data.name,
      email: result.data.email,
      password: result.data.password,
      callbackURL: VERIFY_CALLBACK_URL,
      fetchOptions: { headers: { "x-captcha-response": captchaToken } },
    });

    if (error) {
      setServerError(error.message ?? "Something went wrong. Please try again");
      setLoading(false);
      // Turnstile tokens are single-use — a fresh one is needed before retrying.
      setCaptchaToken("");
      turnstileRef.current?.reset();
      return;
    }

    router.push(`/verify-email?email=${encodeURIComponent(result.data.email)}`);
  }

  return (
    <>
      <Suspense fallback={null}>
        <SourceCapture />
      </Suspense>
      <h1 className="mb-1 text-xl font-medium" style={{ color: "var(--text-primary)" }}>
        Create your account
      </h1>
      <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }}>
        Get your booking page live in under 5 minutes.
      </p>

      {serverError && (
        <div
          className="mb-4 rounded-lg border px-4 py-3 text-sm"
          style={{ borderColor: "var(--danger)", color: "var(--danger)", background: "color-mix(in srgb, var(--danger) 10%, transparent)" }}
        >
          {serverError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text-primary)" }}>
            Full name
          </label>
          <input
            data-testid="signup-name-input"
            name="name"
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={handleChange}
            onBlur={() => validateField("name")}
            placeholder="Jane Smith"
            className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition-colors"
            style={{
              background: "var(--bg-surface)",
              border: `1px solid ${errors.name ? "var(--danger)" : "var(--border)"}`,
              color: "var(--text-primary)",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
            onBlurCapture={(e) => {
              if (!errors.name) e.currentTarget.style.borderColor = "var(--border)";
            }}
          />
          {errors.name && <p className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.name}</p>}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text-primary)" }}>
            Email
          </label>
          <input
            data-testid="signup-email-input"
            name="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={handleChange}
            onBlur={() => validateField("email")}
            placeholder="you@example.com"
            className="w-full rounded-lg px-3 py-2.5 text-sm outline-none transition-colors"
            style={{
              background: "var(--bg-surface)",
              border: `1px solid ${errors.email ? "var(--danger)" : "var(--border)"}`,
              color: "var(--text-primary)",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
            onBlurCapture={(e) => {
              if (!errors.email) e.currentTarget.style.borderColor = "var(--border)";
            }}
          />
          {errors.email && <p className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.email}</p>}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium" style={{ color: "var(--text-primary)" }}>
            Password
          </label>
          <div className="relative">
            <input
              data-testid="signup-password-input"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange}
              onBlur={() => validateField("password")}
              placeholder="Min. 8 characters"
              className="w-full rounded-lg py-2.5 pl-3 pr-10 text-sm outline-none transition-colors"
              style={{
                background: "var(--bg-surface)",
                border: `1px solid ${errors.password ? "var(--danger)" : "var(--border)"}`,
                color: "var(--text-primary)",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
              onBlurCapture={(e) => {
                if (!errors.password) e.currentTarget.style.borderColor = "var(--border)";
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: "var(--text-secondary)" }}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs" style={{ color: "var(--danger)" }}>{errors.password}</p>}

          {form.password && (
            <div className="mt-2">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="h-1 flex-1 rounded-full transition-all duration-200"
                    style={{
                      background: i <= strength.level ? STRENGTH_COLORS[strength.level] : "var(--border)",
                    }}
                  />
                ))}
              </div>
              {strength.label && (
                <p className="mt-1 text-xs" style={{ color: STRENGTH_COLORS[strength.level] }}>
                  {strength.label}
                </p>
              )}
            </div>
          )}
        </div>

        <TurnstileWidget ref={turnstileRef} onToken={setCaptchaToken} onExpire={() => setCaptchaToken("")} />

        <button
          type="submit"
          data-testid="signup-submit-btn"
          disabled={loading || !captchaToken}
          className="mt-2 flex w-full items-center justify-center rounded-lg py-2.5 text-sm font-medium transition-opacity"
          style={{
            background: "var(--accent)",
            color: "var(--accent-fg)",
            opacity: loading || !captchaToken ? 0.5 : 1,
            cursor: loading || !captchaToken ? "not-allowed" : "pointer",
          }}
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
        Already have an account?{" "}
        <Link href="/login" style={{ color: "var(--accent)" }}>
          Sign in
        </Link>
      </p>
    </>
  );
}
